import { motion } from 'framer-motion';
import { FileCheck, Truck, Shield, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout, useLanguage } from '@/components/Layout';
import { ROUTE_PATHS, translations } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const springPresets = {
  gentle: { type: 'spring' as const, stiffness: 300, damping: 35 },
  snappy: { type: 'spring' as const, stiffness: 400, damping: 30 }
};

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: springPresets.gentle
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

export default function Services() {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    {
      icon: FileCheck,
      title: t.services.customsClearance.title,
      description: t.services.customsClearance.description,
      benefits: [
        'Réduction des délais de dédouanement',
        'Conformité garantie aux réglementations',
        'Optimisation des coûts douaniers',
        'Support 24/7 pour vos opérations'
      ]
    },
    {
      icon: Truck,
      title: t.services.transitOperations.title,
      description: t.services.transitOperations.description,
      benefits: [
        'Suivi en temps réel des expéditions',
        'Coordination multimodale',
        'Optimisation des itinéraires',
        'Gestion documentaire complète'
      ]
    },
    {
      icon: Shield,
      title: t.services.compliance.title,
      description: t.services.compliance.description,
      benefits: [
        'Audit complet des procédures',
        'Veille réglementaire continue',
        'Formation personnalisée',
        'Réduction des risques de non-conformité'
      ]
    },
    {
      icon: TrendingUp,
      title: t.services.consulting.title,
      description: t.services.consulting.description,
      benefits: [
        'Analyse approfondie des flux',
        'Stratégie d\'optimisation sur mesure',
        'Négociation avec les autorités',
        'Accompagnement à long terme'
      ]
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Analyse',
      description: 'Évaluation complète de vos besoins et de votre situation actuelle'
    },
    {
      number: '02',
      title: 'Stratégie',
      description: 'Élaboration d\'une solution personnalisée adaptée à vos objectifs'
    },
    {
      number: '03',
      title: 'Mise en œuvre',
      description: 'Déploiement des solutions avec accompagnement de nos experts'
    },
    {
      number: '04',
      title: 'Suivi',
      description: 'Monitoring continu et optimisation des performances'
    }
  ];

  return (
    <Layout>
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.SERVICES_STRATEGY_2}
            alt="Services background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-gradient"
              {...fadeInUp}
            >
              {t.services.title}
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
            >
              {t.services.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 section-gradient">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
          >
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="p-8 h-full card-hover glass-effect">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-2xl icon-glow flex items-center justify-center">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                        <p className="text-muted-foreground mb-6 leading-relaxed">
                          {service.description}
                        </p>
                        <div className="space-y-3">
                          {service.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.SERVICES_STRATEGY_3}
            alt="Process background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Notre Processus de Travail</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Une approche structurée pour garantir votre succès
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
          >
            {processSteps.map((step, index) => (
              <motion.div key={index} variants={staggerItem}>
                <Card className="p-8 h-full glass-effect border-2 hover:border-primary/50 transition-all duration-300">
                  <div className="text-6xl font-bold text-primary/20 mb-4">{step.number}</div>
                  <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 hero-gradient">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à Optimiser Vos Opérations Douanières ?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment nous pouvons vous aider
            </p>
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button size="lg" className="group">
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
